/**
 * ================================================================================================
 *  ONE MOVABLE TILE, FOR BOTH REGIONS OF THE CUSTOMER WORKSPACE
 * ================================================================================================
 *
 * It lived in `TenantHomeScreen.tsx` while the home screen was the only thing that had tiles.
 * **Owner, 2026-11-01:** *"אתה עובר בין מסכים, הוויג׳טים לא אמורים להשתנות."* The market readings
 * are shell furniture now — `shell/MarketRail.tsx` renders them beside `בשימוש לאחרונה` on every
 * customer screen — so the component they are drawn with cannot stay inside one screen.
 *
 * NOTHING ABOUT IT CHANGED IN THE MOVE. The drag model, the keyboard arrows, the hidden-card
 * early return and the handle's composed accessible name are the file it came from, verbatim.
 */

import type { ReactNode } from 'react';

import type { HomeRegion, HomeTileId } from './homeOrder';
import type { TileDrag } from './tileDrag';

export function Tile({
  id,
  labelHe,
  wide = false,
  handleHe,
  region,
  order,
  hidden,
  onMove,
  drag,
  children,
}: {
  readonly id: HomeTileId;
  readonly labelHe: string;
  /** `true` ⇒ the tile spans the whole grid row. A list of eleven reads badly in half a row. */
  readonly wide?: boolean;
  /** What the handle announces: the tile's name and how to move it. */
  readonly handleHe: string;
  /** Which column this tile lives in. A drop never crosses between them -- see `tileDrag.ts`. */
  readonly region: HomeRegion;
  readonly order: readonly string[];
  /** The ids this person switched off. A hidden tile renders nothing at all. */
  readonly hidden: readonly string[];
  readonly onMove: (id: string, delta: -1 | 1) => void;
  /**
   * THE WHOLE-SCREEN DRAG CONTROLLER. **2026-11-01, replacing the browser's drag-and-drop.**
   *
   * `tileDrag.ts` carries the measurement that caused the replacement: in Chrome the old gesture
   * produced `dragstart … dragenter dragover drag dragenter … dragend` with **no `drop` at all**,
   * so the card snapped back every time. Nothing in this component decides anything about the
   * drag any more -- it marks itself with `data-tile-id` and hands its handle four pointer
   * handlers, and the controller hit-tests for the tile under the pointer.
   */
  readonly drag: TileDrag;
  readonly children: ReactNode;
}): JSX.Element | null {
  const index = order.indexOf(id);
  /*
   * A CARD THE PERSON SWITCHED OFF IS NOT RENDERED, rather than hidden with CSS. A `display: none`
   * panel still issues its reads, still appears to a screen reader in some modes, and still
   * occupies the tab order in others -- so "I turned this off" would have been three different
   * truths. There are no hooks above this line, so the early return is safe.
   */
  if (hidden.includes(id)) return null;

  /*
   * AND NEITHER IS A TILE WITH NOTHING IN IT. **Found in the browser, 2026-11-01.**
   *
   * The home screen renders the starred modules as `favouriteModules.length === 0 ? null : (…)`,
   * and its comment says an office that has starred nothing *"sees this tile render NOTHING,
   * which is what `null` from `<Tile>` already does"*. It did not: `null` was the CHILDREN, so the
   * tile still rendered its own box and its own handle — a 44px grip at 0.55 opacity, floating
   * over the corner of the card beside it, topmost at that point and therefore taking the clicks
   * and the drags meant for that card. A tile of height zero with a grip on it.
   *
   * The claim is now true for every caller rather than for none: a tile with no content is not a
   * tile. There are no hooks above this line, so the early return is safe.
   */
  if (children === null || children === undefined || children === false) return null;

  return (
    <div
      className="gp-w-tile"
      style={{ order: index }}
      data-wide={wide ? 'yes' : undefined}
      /*
        THE TWO ATTRIBUTES THE CONTROLLER HIT-TESTS ON. They are the drop model: an element with a
        `data-tile-id` is a tile, and `data-tile-region` is what stops a market reading being
        dropped into a 508px work column. `tileDrag.ts` reads them with `getAttribute` on whatever
        `elementFromPoint` returns, so they must be on the OUTERMOST box of the tile.
      */
      data-tile-id={id}
      data-tile-region={region}
      data-dragging={drag.carrying === id ? 'yes' : undefined}
      /*
        THE DROP TARGET IS THE WHOLE TILE, and the indicator says what will happen: the carried
        tile TAKES THIS ONE'S PLACE. One rule, so there is never a gap between tiles where nothing
        would happen -- *"עם סימון ברור של מקום ההנחה"*.
      */
      data-over={drag.over === id ? 'yes' : undefined}
    >
      {/*
        ── THE HANDLE. **Section 2, 2026-10-28.** ────────────────────────────────────────────────

        `draggable` sits on the HANDLE and not on the tile, so selecting text inside a panel still
        works and a drag can only start from the thing that looks like a grip.

        IT IS A BUTTON BECAUSE A DRAG IS NOT AN INTERFACE ON ITS OWN. Focus it and the arrow keys
        move the tile -- the Owner asked for the arrows to leave the normal view, not for the
        keyboard to lose the feature. `aria-label` carries the tile's name and the instruction,
        because "handle" alone is meaningless in a column of four.
      */}
      <button
        type="button"
        className="gp-w-tile__handle"
        /*
          THE NAME OF THIS TILE, THEN THE INSTRUCTION. **2026-10-28.** `labelHe` was passed by
          every call site and READ BY NOTHING, so all four handles announced the same generic
          *"שינוי מיקום"* — and the comment above claimed otherwise. In a grid of fifteen
          that is fifteen identical buttons, which is unusable by keyboard and a lie in the
          comment. The composition is here rather than at the call sites so there is one of it.
        */
        aria-label={`${labelHe} — ${handleHe}`}
        /*
          ── POINTER EVENTS, NOT `draggable`. **2026-11-01.** ────────────────────────────────────

          Four handlers and no `draggable` attribute. The reasoning, the browser event trace that
          forced it and what it buys on a touch screen are all in `shell/tileDrag.ts`; the short
          form is that the browser's own drag-and-drop never dispatched a `drop` here, cannot be
          driven by any instrument this repository owns, and does not exist on a phone.

          `touch-action: none` on this button in the stylesheet is the other half: without it a
          finger on the grip scrolls the page instead of carrying the card.
        */
        onPointerDown={(e) => drag.onPointerDown(e, id, region)}
        onPointerMove={drag.onPointerMove}
        onPointerUp={drag.onPointerUp}
        onPointerCancel={drag.onPointerCancel}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' && index > 0) {
            e.preventDefault();
            onMove(id, -1);
          }
          if (e.key === 'ArrowDown' && index < order.length - 1) {
            e.preventDefault();
            onMove(id, 1);
          }
        }}
      >
        {/* Six dots: the grip the Owner drew, and it is decorative — the name is on the button. */}
        <span aria-hidden="true">⠿</span>
      </button>
      {children}
    </div>
  );
}
